// ═══════════════════════════════════════════════════════════════════
//  modules/luckperms/LuckPerms.js
//  Module « LuckPerms » — les grades LuckPerms, en tableau.
//
//  Consultable par les admins et les fondateurs. Seul un super_admin
//  peut appliquer ; le backend le revérifie de toute façon :
//  l'interface cache, elle ne protège pas.
//
//  Le principe : on coche librement, rien ne part. La barre du bas
//  montre les commandes qui vont être envoyées, et un seul bouton
//  les envoie.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsMobile } from "../../socle/hooks";
import { THEME } from "../../socle/theme";
import { luckpermsApi, suivante } from "./api";
import { LpMatrice } from "./LpMatrice";
import { LpPanier } from "./LpPanier";
import { LpJournal } from "./LpJournal";

export function LuckPerms({ token, user }) {
  const isMobile = useIsMobile();
  const api = useMemo(() => luckpermsApi(token), [token]);

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState("matrice");
  const [filtre, setFiltre] = useState("");
  const [brouillon, setBrouillon] = useState({});   // brouillon[node][grade] = valeur
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);

  const peutEcrire = user && user.role === "super_admin";

  const charger = useCallback(() => {
    setChargement(true);
    api.etat()
      .then((d) => { setDonnees(d); setErreur(null); })
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [api]);

  useEffect(() => { charger(); }, [charger]);

  // ── Le panier ──
  const changements = useMemo(() => {
    const out = [];
    for (const node of Object.keys(brouillon))
      for (const grade of Object.keys(brouillon[node]))
        out.push({ node, grade, valeur: brouillon[node][grade] });
    return out;
  }, [brouillon]);

  // La valeur réellement POSÉE sur ce grade — pas la valeur effective.
  // Une case héritée n'a rien de posé : c'est « unset ». Sans cette
  // distinction, remettre « absente » sur une case héritée produirait une
  // commande qui ne change rien.
  const posee = useCallback((node, grade) => {
    const reel = ((donnees && donnees.etat[node]) || {})[grade];
    if (!reel) return "unset";
    return reel.h ? "unset" : reel.v;
  }, [donnees]);

  const poser = useCallback((node, grade, valeur) => {
    setBrouillon((b) => {
      const copie = { ...b, [node]: { ...(b[node] || {}) } };
      if (valeur === posee(node, grade)) delete copie[node][grade];
      else copie[node][grade] = valeur;
      if (!Object.keys(copie[node]).length) delete copie[node];
      return copie;
    });
  }, [posee]);

  const basculer = useCallback((node, grade, valeurAffichee) => {
    if (!peutEcrire) return;
    // Sur une case héritée, l'étape suivante du cycle serait « absente » —
    // qui est déjà ce qui est posé sur ce grade. La case reviendrait donc à
    // ce qu'elle affiche, et on croirait le bouton cassé. On saute cette
    // étape-là.
    const effective = (((donnees && donnees.etat[node]) || {})[grade] || {}).v || "unset";
    let suiv = suivante(valeurAffichee);
    if (suiv === posee(node, grade) && effective === valeurAffichee) suiv = suivante(suiv);
    poser(node, grade, suiv);
  }, [poser, posee, peutEcrire, donnees]);

  const proposer = useCallback((lignes) => {
    if (!peutEcrire || !donnees) return;
    setBrouillon((b) => {
      const copie = { ...b };
      for (const c of lignes) {
        if (c.jamais) continue;
        const prop = c.propose || {};
        for (const g of donnees.grades) {
          const voulu = prop[g.cle] || "unset";
          const reel = (donnees.etat[c.node] || {})[g.cle] || { v: "unset", h: null };
          const dejaPose = reel.h ? "unset" : reel.v;
          if (voulu === dejaPose) { if (copie[c.node]) delete copie[c.node][g.cle]; continue; }
          copie[c.node] = { ...(copie[c.node] || {}), [g.cle]: voulu };
        }
        if (copie[c.node] && !Object.keys(copie[c.node]).length) delete copie[c.node];
      }
      return copie;
    });
  }, [donnees, peutEcrire]);

  const retirer = useCallback((node, grade) => {
    setBrouillon((b) => {
      const copie = { ...b, [node]: { ...(b[node] || {}) } };
      delete copie[node][grade];
      if (!Object.keys(copie[node]).length) delete copie[node];
      return copie;
    });
  }, []);

  const appliquer = useCallback((motif) => {
    setEnvoi(true);
    api.appliquer(changements, motif)
      .then((r) => { setResultat(r); setBrouillon({}); charger(); })
      .catch((e) => setResultat({
        applique: 0, sans_effet: 0, echecs: 1, resultats: [], lot: "—", serveur: "—",
        refus: [{ node: "—", grade: "—", raison: e.message }],
      }))
      .finally(() => setEnvoi(false));
  }, [api, changements, charger]);

  // ── Rendu ──
  if (chargement && !donnees)
    return <div style={{ padding: 40, color: THEME.textDim }}>Lecture de LuckPerms…</div>;

  if (erreur && !donnees)
    return (
      <div style={{ padding: 40 }}>
        <div style={{ color: THEME.red, marginBottom: 10 }}>{erreur}</div>
        <button style={btn} onClick={charger}>Réessayer</button>
      </div>
    );

  const { grades, catalogue, etat, inconnus, grades_manquants, serveur_rcon } = donnees;

  return (
    <div style={{ paddingBottom: 150 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 650, color: THEME.text }}>
          LuckPerms
        </h1>
        <span style={{ color: THEME.textDim, fontSize: 13 }}>
          {catalogue.length} permissions · {grades.length} grades · commandes envoyées sur <b>{serveur_rcon}</b>
        </span>
      </div>
      <div style={{ color: THEME.textMuted, fontSize: 13, marginBottom: 18 }}>
        L'état affiché est lu dans LuckPerms, en direct. Les cases pâles sont héritées du grade du dessous.
      </div>

      {!peutEcrire && (
        <Bandeau couleur={THEME.amber}>
          Lecture seule : seul le rôle <b>super_admin</b> peut appliquer un changement.
        </Bandeau>
      )}

      {grades_manquants && grades_manquants.length > 0 && (
        <Bandeau couleur={THEME.red}>
          {grades_manquants.length} grade(s) n'existe(nt) pas encore dans LuckPerms :{" "}
          <code>{grades_manquants.join(", ")}</code>. Crée-les d'abord dans la Console —{" "}
          <code>lp creategroup {grades_manquants[0]}</code> — sinon les commandes de ce module
          seront refusées par le serveur.
        </Bandeau>
      )}

      {inconnus && inconnus.length > 0 && (
        <Bandeau couleur={THEME.amber}>
          <b>{inconnus.length} permission(s) posée(s) sur un grade sans être au catalogue.</b>{" "}
          Quelqu'un les a accordées à la main, ou un plugin nouveau les a apportées :{" "}
          <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            {inconnus.slice(0, 12).map((i) => (
              <code key={i.node} style={{
                background: THEME.surface, border: `1px solid ${THEME.border}`,
                borderRadius: 5, padding: "1px 6px",
              }}>
                {i.node}
              </code>
            ))}
            {inconnus.length > 12 ? <span style={{ color: THEME.textDim }}>… et {inconnus.length - 12} autre(s)</span> : null}
          </span>
        </Bandeau>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "16px 0 14px" }}>
        <button style={onglet === "matrice" ? btnOn : btn} onClick={() => setOnglet("matrice")}>Matrice</button>
        <button style={onglet === "journal" ? btnOn : btn} onClick={() => setOnglet("journal")}>Journal</button>
        <span style={{ flex: 1 }} />
        {onglet === "matrice" && (
          <input
            value={filtre}
            onChange={(e) => setFiltre(e.target.value)}
            placeholder="Filtrer : node, plugin, domaine…"
            style={{
              background: THEME.surface, border: `1px solid ${THEME.border}`,
              borderRadius: 7, color: THEME.text, padding: "7px 11px",
              fontSize: 13, minWidth: isMobile ? 130 : 240, flex: isMobile ? 1 : "none",
            }}
          />
        )}
        <button style={btn} onClick={charger}>Actualiser</button>
      </div>

      {onglet === "matrice" ? (
        <LpMatrice
          grades={grades} catalogue={catalogue} etat={etat} brouillon={brouillon}
          onBasculer={basculer} onProposer={proposer} filtre={filtre} isMobile={isMobile}
        />
      ) : (
        <LpJournal api={api} grades={grades} />
      )}

      <LpPanier
        changements={changements} grades={grades} etat={etat}
        enCours={envoi} resultat={resultat} isMobile={isMobile}
        onFermerResultat={() => setResultat(null)}
        onVider={() => setBrouillon({})}
        onRetirer={retirer}
        onAppliquer={appliquer}
      />
    </div>
  );
}

function Bandeau({ couleur, children }) {
  return (
    <div style={{
      background: THEME.panel,
      border: `1px solid ${THEME.border}`,
      borderLeft: `3px solid ${couleur}`,
      borderRadius: 8, padding: "11px 14px", marginBottom: 10,
      color: THEME.text, fontSize: 13, lineHeight: 1.6,
    }}>
      {children}
    </div>
  );
}

const btn = {
  background: THEME.panel, border: `1px solid ${THEME.border}`, color: THEME.text,
  borderRadius: 7, padding: "7px 13px", fontSize: 13, cursor: "pointer",
};
const btnOn = { ...btn, background: THEME.accent, borderColor: THEME.accent, color: "#fff", fontWeight: 600 };
